/**
 * Flock Health Assistant
 *
 * Comprehensive health diagnostic tool with three modules:
 * 1. Mortality Analysis - "What killed my bird?"
 * 2. Droppings Checker - Fecal analysis
 * 3. Symptom Checker - Live bird symptoms
 *
 * Features automatic weather-integrated water alerts.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './FlockHealthAssistant.css';

// Types
interface SymptomCategory {
  key: string;
  display: string;
}

interface MortalitySign {
  key: string;
  display: string;
}

interface FecalAppearance {
  key: string;
  display: string;
  category: string;
}

interface WeatherRisk {
  risk_level: string;
  message?: string;
  recommendation?: string;
  water_multiplier: number;
  check_water_first: boolean;
  risk_factors?: Array<{
    factor: string;
    severity: string;
    details: string;
    impact: string;
  }>;
  weather_summary?: {
    max_temp_7_days: number;
    avg_high_7_days: number;
    min_temp_7_days: number;
    extreme_heat_days: number;
    high_heat_days: number;
    freezing_days: number;
  };
}

interface HealthOptions {
  symptom_categories: Record<string, SymptomCategory[]>;
  mortality_signs: {
    visual: MortalitySign[];
    circumstances: MortalitySign[];
    bird_type: MortalitySign[];
  };
  fecal_appearances: FecalAppearance[];
  weather_risk: WeatherRisk;
}

interface Mitigation {
  action: string;
  details: string;
}

interface MortalityCause {
  cause_id: string;
  name: string;
  confidence: string;
  matching_signs: string[];
  description: string;
  simple_mitigations: Mitigation[];
  when_to_worry?: string;
  vet_needed: boolean | string;
  not_contagious?: boolean;
  contagious?: boolean;
  reportable?: boolean;
  reportable_warning?: string;
}

interface MortalityResult {
  analysis_type: string;
  disclaimer: string;
  environmental_causes: MortalityCause[];
  metabolic_causes: MortalityCause[];
  disease_causes: MortalityCause[];
  check_first_message?: string;
  water_alerts: WaterAlert[];
  vet_needed: boolean;
  reportable_warning?: string;
  key_messages: string[];
}

interface DroppingsIndicator {
  indicator_id: string;
  appearance: string;
  category: string;
  matching_tags: string[];
  description?: string;
  action: string;
  when_concerning?: string;
  simple_mitigations?: Mitigation[];
  likely_causes?: string[];
  vet_needed?: boolean | string;
  important_note?: string;
}

interface DroppingsResult {
  analysis_type: string;
  disclaimer: string;
  matched_indicators: DroppingsIndicator[];
  is_normal: boolean;
  urgency: string;
  action_needed: boolean;
  vet_needed: boolean;
}

interface SymptomCheckFirst {
  cause: string;
  likelihood: string;
  other_signs?: string[];
  simple_mitigations?: Mitigation[];
  action?: string;
  when_not_this?: string;
  weather_match?: boolean;
  weather_note?: string;
}

interface SymptomDisease {
  disease: string;
  other_signs?: string[];
  action: string;
  contagious?: boolean;
  important_note?: string;
  reportable?: boolean;
}

interface SymptomAnalysis {
  symptom: string;
  category: string;
  check_first: SymptomCheckFirst[];
  could_be_illness: SymptomDisease[];
  vet_trigger?: string;
  investigate_further?: string[];
  urgency?: string;
  message?: string;
}

interface SymptomResult {
  analysis_type: string;
  disclaimer: string;
  symptom_analyses: SymptomAnalysis[];
  water_alerts: WaterAlert[];
  vet_needed: boolean;
  reportable_warning?: string;
  overall_urgency: string;
}

interface WaterAlert {
  level: string;
  class: string;
  title: string;
  message: string;
  action_items: string[];
  weather_data?: Record<string, number>;
  symptom_correlation?: {
    title: string;
    details: string[];
  };
  important_note?: string;
}

interface WaterRequirements {
  minimum_gallons_daily: number;
  recommended_capacity: number;
  water_access_points: number;
  check_frequency: string;
  cups_per_bird: number;
}

type Tab = 'mortality' | 'droppings' | 'symptoms';

const FlockHealthAssistant: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('symptoms');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<HealthOptions | null>(null);

  // Form states
  const [selectedSigns, setSelectedSigns] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [flockSize, setFlockSize] = useState<number | ''>('');
  const [birdAge, setBirdAge] = useState<number | ''>('');
  const [birdType, setBirdType] = useState('');

  // Results
  const [mortalityResult, setMortalityResult] = useState<MortalityResult | null>(null);
  const [droppingsResult, setDroppingsResult] = useState<DroppingsResult | null>(null);
  const [symptomResult, setSymptomResult] = useState<SymptomResult | null>(null);
  const [waterRequirements, setWaterRequirements] = useState<WaterRequirements | null>(null);

  // Load options on mount
  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      const data = await bridgeClubAPI.getHealthOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to load health options:', err);
      setError('Failed to load health options');
    }
  };

  const handleAnalyzeMortality = async () => {
    if (selectedSigns.length === 0) {
      setError('Please select at least one observed sign');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await bridgeClubAPI.analyzeMortality({
        signs: selectedSigns,
        bird_info: birdAge || birdType ? {
          age_weeks: birdAge || undefined,
          type: birdType || undefined,
        } : undefined,
        flock_size: flockSize || undefined,
      });
      setMortalityResult(result);
      if (result.water_requirements) {
        setWaterRequirements(result.water_requirements);
      }
    } catch (err: unknown) {
      const errorObj = err as { error?: string; detail?: string };
      setError(errorObj.error || errorObj.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeDroppings = async () => {
    if (selectedTags.length === 0) {
      setError('Please select at least one appearance tag');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await bridgeClubAPI.analyzeDroppings({
        tags: selectedTags,
      });
      setDroppingsResult(result);
    } catch (err: unknown) {
      const errorObj = err as { error?: string; detail?: string };
      setError(errorObj.error || errorObj.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckSymptoms = async () => {
    if (selectedSymptoms.length === 0) {
      setError('Please select at least one symptom');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await bridgeClubAPI.checkSymptoms({
        symptoms: selectedSymptoms,
        bird_info: birdAge || birdType ? {
          age_weeks: birdAge || undefined,
          type: birdType || undefined,
        } : undefined,
        flock_size: flockSize || undefined,
      });
      setSymptomResult(result);
      if (result.water_requirements) {
        setWaterRequirements(result.water_requirements);
      }
    } catch (err: unknown) {
      const errorObj = err as { error?: string; detail?: string };
      setError(errorObj.error || errorObj.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSign = (sign: string) => {
    setSelectedSigns(prev =>
      prev.includes(sign) ? prev.filter(s => s !== sign) : [...prev, sign]
    );
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleSymptom = (symptom: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptom) ? prev.filter(s => s !== symptom) : [...prev, symptom]
    );
  };

  const resetForm = () => {
    setSelectedSigns([]);
    setSelectedTags([]);
    setSelectedSymptoms([]);
    setMortalityResult(null);
    setDroppingsResult(null);
    setSymptomResult(null);
    setWaterRequirements(null);
    setError(null);
  };

  const getConfidenceClass = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      default: return 'confidence-low';
    }
  };

  const getUrgencyClass = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'urgency-high';
      case 'moderate': return 'urgency-moderate';
      default: return 'urgency-normal';
    }
  };

  const renderWaterAlertBanner = () => {
    if (!options?.weather_risk) return null;
    const risk = options.weather_risk;

    if (risk.risk_level === 'unknown' || risk.risk_level === 'low') return null;

    const alertClass = risk.risk_level === 'critical' ? 'alert-critical' :
                       risk.risk_level === 'high' ? 'alert-high' : 'alert-moderate';

    return (
      <div className={`water-alert-banner ${alertClass}`}>
        <div className="alert-icon">
          {risk.risk_level === 'critical' ? '🚨' : risk.risk_level === 'high' ? '⚠️' : '💧'}
        </div>
        <div className="alert-content">
          <strong>
            {risk.risk_level === 'critical' ? 'CRITICAL WATER ALERT' :
             risk.risk_level === 'high' ? 'HIGH WATER RISK' : 'WATER CHECK RECOMMENDED'}
          </strong>
          {risk.risk_factors && risk.risk_factors.length > 0 && (
            <p>{risk.risk_factors[0].impact}</p>
          )}
          {risk.weather_summary && (
            <span className="weather-summary">
              Past 7 days: High {risk.weather_summary.avg_high_7_days.toFixed(0)}°F avg,
              Max {risk.weather_summary.max_temp_7_days.toFixed(0)}°F
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderWaterAlerts = (alerts: WaterAlert[]) => {
    if (!alerts || alerts.length === 0) return null;

    return (
      <div className="water-alerts-section">
        {alerts.map((alert, idx) => (
          <div key={idx} className={`water-alert ${alert.class}`}>
            <div className="alert-header">
              <span className="alert-level">{alert.level}</span>
              <span className="alert-title">{alert.title}</span>
            </div>
            <p className="alert-message">{alert.message}</p>
            {alert.action_items && alert.action_items.length > 0 && (
              <ul className="alert-actions">
                {alert.action_items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
            {alert.symptom_correlation && (
              <div className="symptom-correlation">
                <strong>{alert.symptom_correlation.title}</strong>
                <ul>
                  {alert.symptom_correlation.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            )}
            {alert.important_note && (
              <p className="important-note">{alert.important_note}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderWaterRequirements = () => {
    if (!waterRequirements) return null;

    return (
      <div className="water-requirements-card">
        <h4>Water Requirements for Your Flock</h4>
        <div className="requirements-grid">
          <div className="req-item">
            <span className="req-label">Daily Minimum</span>
            <span className="req-value">{waterRequirements.minimum_gallons_daily} gal</span>
          </div>
          <div className="req-item">
            <span className="req-label">Recommended Capacity</span>
            <span className="req-value">{waterRequirements.recommended_capacity} gal</span>
          </div>
          <div className="req-item">
            <span className="req-label">Water Stations</span>
            <span className="req-value">{waterRequirements.water_access_points} minimum</span>
          </div>
          <div className="req-item">
            <span className="req-label">Check Frequency</span>
            <span className="req-value">{waterRequirements.check_frequency}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderMortalityForm = () => (
    <div className="analysis-form">
      <h3>What did you observe?</h3>
      <p className="form-hint">Select all signs that apply. We'll check environmental causes first.</p>

      {options?.mortality_signs.visual && (
        <div className="sign-category">
          <h4>Visual Signs</h4>
          <div className="sign-buttons">
            {options.mortality_signs.visual.map(sign => (
              <button
                key={sign.key}
                className={`sign-btn ${selectedSigns.includes(sign.key) ? 'selected' : ''}`}
                onClick={() => toggleSign(sign.key)}
              >
                {sign.display}
              </button>
            ))}
          </div>
        </div>
      )}

      {options?.mortality_signs.bird_type && (
        <div className="sign-category">
          <h4>Bird Type</h4>
          <div className="sign-buttons">
            {options.mortality_signs.bird_type.map(type => (
              <button
                key={type.key}
                className={`sign-btn ${selectedSigns.includes(type.key) ? 'selected' : ''}`}
                onClick={() => toggleSign(type.key)}
              >
                {type.display}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="optional-fields">
        <h4>Optional Details</h4>
        <div className="field-row">
          <label>
            Flock Size:
            <input
              type="number"
              value={flockSize}
              onChange={(e) => setFlockSize(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="e.g., 25"
            />
          </label>
          <label>
            Bird Age (weeks):
            <input
              type="number"
              value={birdAge}
              onChange={(e) => setBirdAge(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="e.g., 16"
            />
          </label>
        </div>
      </div>

      <div className="form-buttons">
        <button
          className="analyze-btn"
          onClick={handleAnalyzeMortality}
          disabled={loading || selectedSigns.length === 0}
        >
          {loading ? 'Analyzing...' : 'Analyze Mortality'}
        </button>
        <button
          className="cancel-btn"
          onClick={() => navigate('/bridge-club/resources')}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderMortalityResult = () => {
    if (!mortalityResult) return null;

    return (
      <div className="analysis-result">
        <div className="result-header">
          <h3>Analysis Results</h3>
          <button className="reset-btn" onClick={resetForm}>Start Over</button>
        </div>

        <div className="disclaimer">{mortalityResult.disclaimer}</div>

        {mortalityResult.reportable_warning && (
          <div className="reportable-warning">
            <strong>REPORTABLE DISEASE WARNING</strong>
            <p>{mortalityResult.reportable_warning}</p>
          </div>
        )}

        {renderWaterAlerts(mortalityResult.water_alerts)}
        {renderWaterRequirements()}

        {mortalityResult.check_first_message && (
          <div className="check-first-message">
            <strong>Check First:</strong> {mortalityResult.check_first_message}
          </div>
        )}

        {mortalityResult.environmental_causes.length > 0 && (
          <div className="causes-section environmental">
            <h4>Environmental/Management Causes (Check First)</h4>
            {mortalityResult.environmental_causes.map(cause => (
              <div key={cause.cause_id} className={`cause-card ${getConfidenceClass(cause.confidence)}`}>
                <div className="cause-header">
                  <span className="cause-name">{cause.name}</span>
                  <span className={`confidence-badge ${cause.confidence}`}>{cause.confidence} match</span>
                </div>
                <p className="cause-description">{cause.description}</p>
                <div className="matching-signs">
                  <strong>Matching signs:</strong> {cause.matching_signs.join(', ')}
                </div>
                {cause.simple_mitigations.length > 0 && (
                  <div className="mitigations">
                    <strong>What to do:</strong>
                    <ul>
                      {cause.simple_mitigations.map((m, i) => (
                        <li key={i}><strong>{m.action}:</strong> {m.details}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {cause.when_to_worry && (
                  <div className="when-to-worry">
                    <strong>When to worry:</strong> {cause.when_to_worry}
                  </div>
                )}
                {cause.not_contagious && (
                  <span className="not-contagious-badge">Not Contagious</span>
                )}
              </div>
            ))}
          </div>
        )}

        {mortalityResult.metabolic_causes.length > 0 && (
          <div className="causes-section metabolic">
            <h4>Metabolic/Genetic Causes</h4>
            {mortalityResult.metabolic_causes.map(cause => (
              <div key={cause.cause_id} className={`cause-card ${getConfidenceClass(cause.confidence)}`}>
                <div className="cause-header">
                  <span className="cause-name">{cause.name}</span>
                  <span className={`confidence-badge ${cause.confidence}`}>{cause.confidence} match</span>
                </div>
                <p className="cause-description">{cause.description}</p>
                {cause.simple_mitigations.length > 0 && (
                  <div className="mitigations">
                    <strong>Prevention:</strong>
                    <ul>
                      {cause.simple_mitigations.map((m, i) => (
                        <li key={i}><strong>{m.action}:</strong> {m.details}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {mortalityResult.disease_causes.length > 0 && (
          <div className="causes-section disease">
            <h4>Disease Causes (Rule out management first)</h4>
            {mortalityResult.disease_causes.map(cause => (
              <div key={cause.cause_id} className={`cause-card ${getConfidenceClass(cause.confidence)}`}>
                <div className="cause-header">
                  <span className="cause-name">{cause.name}</span>
                  <span className={`confidence-badge ${cause.confidence}`}>{cause.confidence} match</span>
                  {cause.contagious && <span className="contagious-badge">Contagious</span>}
                  {cause.reportable && <span className="reportable-badge">REPORTABLE</span>}
                </div>
                <p className="cause-description">{cause.description}</p>
                {cause.vet_needed && (
                  <div className="vet-needed">
                    <strong>Vet recommendation:</strong> {typeof cause.vet_needed === 'string' ? cause.vet_needed : 'Consult a veterinarian'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDroppingsForm = () => (
    <div className="analysis-form">
      <h3>Describe the droppings</h3>
      <p className="form-hint">Select all appearance characteristics that match.</p>

      <div className="appearance-category">
        {options?.fecal_appearances && (
          <div className="appearance-buttons">
            {options.fecal_appearances.map(app => (
              <button
                key={app.key}
                className={`appearance-btn ${selectedTags.includes(app.key) ? 'selected' : ''} category-${app.category}`}
                onClick={() => toggleTag(app.key)}
              >
                {app.display}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="form-buttons">
        <button
          className="analyze-btn"
          onClick={handleAnalyzeDroppings}
          disabled={loading || selectedTags.length === 0}
        >
          {loading ? 'Analyzing...' : 'Analyze Droppings'}
        </button>
        <button
          className="cancel-btn"
          onClick={() => navigate('/bridge-club/resources')}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderDroppingsResult = () => {
    if (!droppingsResult) return null;

    return (
      <div className="analysis-result">
        <div className="result-header">
          <h3>Droppings Analysis</h3>
          <button className="reset-btn" onClick={resetForm}>Start Over</button>
        </div>

        <div className="disclaimer">{droppingsResult.disclaimer}</div>

        {droppingsResult.is_normal && (
          <div className="normal-indicator">
            This appears to be normal or diet-related variation!
          </div>
        )}

        {droppingsResult.urgency !== 'none' && (
          <div className={`urgency-indicator ${getUrgencyClass(droppingsResult.urgency)}`}>
            Urgency: {droppingsResult.urgency.toUpperCase()}
          </div>
        )}

        {droppingsResult.matched_indicators.map((indicator, idx) => (
          <div key={idx} className={`indicator-card category-${indicator.category}`}>
            <div className="indicator-header">
              <span className="indicator-appearance">{indicator.appearance}</span>
              <span className={`category-badge ${indicator.category}`}>{indicator.category}</span>
            </div>
            {indicator.description && <p>{indicator.description}</p>}
            <div className="indicator-action">
              <strong>Action:</strong> {indicator.action}
            </div>
            {indicator.when_concerning && (
              <div className="when-concerning">
                <strong>When to be concerned:</strong> {indicator.when_concerning}
              </div>
            )}
            {indicator.simple_mitigations && indicator.simple_mitigations.length > 0 && (
              <div className="mitigations">
                <strong>What to do:</strong>
                <ul>
                  {indicator.simple_mitigations.map((m, i) => (
                    <li key={i}><strong>{m.action}:</strong> {m.details}</li>
                  ))}
                </ul>
              </div>
            )}
            {indicator.important_note && (
              <div className="important-note">{indicator.important_note}</div>
            )}
            {indicator.vet_needed && (
              <div className="vet-needed">
                <strong>Vet:</strong> {typeof indicator.vet_needed === 'string' ? indicator.vet_needed : 'Consider consulting a vet'}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSymptomsForm = () => (
    <div className="analysis-form">
      <h3>What symptoms are you seeing?</h3>
      <p className="form-hint">Select all symptoms you've observed in your bird.</p>

      {options?.symptom_categories && Object.entries(options.symptom_categories).map(([category, symptoms]) => (
        <div key={category} className="symptom-category">
          <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
          <div className="symptom-buttons">
            {symptoms.map(symptom => (
              <button
                key={symptom.key}
                className={`symptom-btn ${selectedSymptoms.includes(symptom.key) ? 'selected' : ''}`}
                onClick={() => toggleSymptom(symptom.key)}
              >
                {symptom.display}
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="optional-fields">
        <h4>Optional Details</h4>
        <div className="field-row">
          <label>
            Flock Size:
            <input
              type="number"
              value={flockSize}
              onChange={(e) => setFlockSize(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="e.g., 25"
            />
          </label>
          <label>
            Bird Age (weeks):
            <input
              type="number"
              value={birdAge}
              onChange={(e) => setBirdAge(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="e.g., 16"
            />
          </label>
        </div>
      </div>

      <div className="form-buttons">
        <button
          className="analyze-btn"
          onClick={handleCheckSymptoms}
          disabled={loading || selectedSymptoms.length === 0}
        >
          {loading ? 'Checking...' : 'Check Symptoms'}
        </button>
        <button
          className="cancel-btn"
          onClick={() => navigate('/bridge-club/resources')}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderSymptomsResult = () => {
    if (!symptomResult) return null;

    return (
      <div className="analysis-result">
        <div className="result-header">
          <h3>Symptom Analysis</h3>
          <button className="reset-btn" onClick={resetForm}>Start Over</button>
        </div>

        <div className="disclaimer">{symptomResult.disclaimer}</div>

        {symptomResult.reportable_warning && (
          <div className="reportable-warning">
            <strong>IMPORTANT</strong>
            <p>{symptomResult.reportable_warning}</p>
          </div>
        )}

        {symptomResult.overall_urgency === 'high' && (
          <div className="urgency-indicator urgency-high">
            HIGH URGENCY - Prompt action recommended
          </div>
        )}

        {renderWaterAlerts(symptomResult.water_alerts)}
        {renderWaterRequirements()}

        {symptomResult.symptom_analyses.map((analysis, idx) => (
          <div key={idx} className="symptom-analysis-card">
            <h4>{analysis.symptom}</h4>

            {analysis.check_first && analysis.check_first.length > 0 && (
              <div className="check-first-section">
                <h5>Check These First (Most Common Causes)</h5>
                {analysis.check_first.map((cause, i) => (
                  <div key={i} className="check-first-item">
                    <div className="cause-header-line">
                      <strong>{cause.cause}</strong>
                      {cause.likelihood && <span className="likelihood-badge">{cause.likelihood}</span>}
                      {cause.weather_match && <span className="weather-match-badge">Weather Match</span>}
                    </div>
                    {cause.weather_note && <p className="weather-note">{cause.weather_note}</p>}
                    {cause.other_signs && cause.other_signs.length > 0 && (
                      <div className="other-signs">
                        <span>Also look for:</span> {cause.other_signs.join(', ')}
                      </div>
                    )}
                    {cause.action && <p className="cause-action">{cause.action}</p>}
                    {cause.simple_mitigations && cause.simple_mitigations.length > 0 && (
                      <ul className="mitigations-list">
                        {cause.simple_mitigations.map((m, j) => (
                          <li key={j}><strong>{m.action}:</strong> {m.details}</li>
                        ))}
                      </ul>
                    )}
                    {cause.when_not_this && (
                      <p className="when-not-this">
                        <em>Not likely if:</em> {cause.when_not_this}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {analysis.could_be_illness && analysis.could_be_illness.length > 0 && (
              <div className="illness-section">
                <h5>Could Also Be (If common causes ruled out)</h5>
                {analysis.could_be_illness.map((disease, i) => (
                  <div key={i} className="illness-item">
                    <div className="disease-header">
                      <strong>{disease.disease}</strong>
                      {disease.contagious && <span className="contagious-badge">Contagious</span>}
                      {disease.reportable && <span className="reportable-badge">REPORTABLE</span>}
                    </div>
                    {disease.other_signs && disease.other_signs.length > 0 && (
                      <div className="other-signs">
                        <span>Other signs:</span> {disease.other_signs.join(', ')}
                      </div>
                    )}
                    <p className="disease-action">{disease.action}</p>
                    {disease.important_note && (
                      <p className="important-note">{disease.important_note}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {analysis.vet_trigger && (
              <div className="vet-trigger">
                <strong>When to see a vet:</strong> {analysis.vet_trigger}
              </div>
            )}

            {analysis.investigate_further && analysis.investigate_further.length > 0 && (
              <div className="investigate-further">
                <strong>Things to check:</strong>
                <ul>
                  {analysis.investigate_further.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flock-health-assistant">
      <div className="header">
        <h2>Flock Health Assistant</h2>
        <p>Get guidance on flock health issues with weather-aware recommendations</p>
      </div>

      {renderWaterAlertBanner()}

      {error && <div className="error-message">{error}</div>}

      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'symptoms' ? 'active' : ''}`}
          onClick={() => { setActiveTab('symptoms'); resetForm(); }}
        >
          Symptom Checker
        </button>
        <button
          className={`tab-btn ${activeTab === 'mortality' ? 'active' : ''}`}
          onClick={() => { setActiveTab('mortality'); resetForm(); }}
        >
          Mortality Analysis
        </button>
        <button
          className={`tab-btn ${activeTab === 'droppings' ? 'active' : ''}`}
          onClick={() => { setActiveTab('droppings'); resetForm(); }}
        >
          Droppings Checker
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'symptoms' && (
          symptomResult ? renderSymptomsResult() : renderSymptomsForm()
        )}
        {activeTab === 'mortality' && (
          mortalityResult ? renderMortalityResult() : renderMortalityForm()
        )}
        {activeTab === 'droppings' && (
          droppingsResult ? renderDroppingsResult() : renderDroppingsForm()
        )}
      </div>
    </div>
  );
};

export default FlockHealthAssistant;
